import {
  ActionIcon,
  Badge,
  Button,
  CloseButton,
  FileButton,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useWindowEvent } from "@mantine/hooks";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  deleteVoiceProfile,
  getListVoiceProfilesQueryKey,
  listVoiceProfiles,
  quoteJob,
  startJob,
} from "../../../api/generated/endpoints/generation/generation";
import type { Asset, VoiceProfile } from "../../../api/generated/model";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { ErrorAlert } from "../../ErrorAlert";
import { useIdempotencyKey } from "../../idempotency";
import { UploadState, useMediaUpload } from "../../media/media-upload";
import { toUserMessage } from "../../messages";
import { VoicePlay } from "../../voices/VoiceList";
import classes from "../../voices/voices.module.css";

type Source = "voices" | "create";

const SOURCES: { value: Source; label: string }[] = [
  { value: "voices", label: "Kling Voices" },
  { value: "create", label: "Create Voice" },
];

/** FAL's four clip types for a voice (spec G2-kling-v4-voices). */
const CLIP_TYPES = ["audio/mpeg", "audio/wav", "video/mp4", "video/quicktime"];
const CLIP_KINDS = ["audio", "video"] as const;
const CLIP_RULES =
  "One clip of one clear speaker: MP3, WAV, MP4 or MOV, 5 to 30 seconds, under 50 MB.";
// A new upload's length is read by the worker a moment after it lands: its quote waits for it.
const MEASURE_TRIES = 15;
const MEASURE_WAIT_MS = 2000;
/** The longest voice name the server keeps (`MAX_VOICE_NAME`). */
const MAX_NAME = 99;

/** Whether a refusal says only that the clip's length is not read yet. */
export function notMeasuredYet(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 422) return false;
  const errors: unknown = error.details.errors;
  return (
    Array.isArray(errors) &&
    errors.some(
      (each: unknown) =>
        typeof each === "object" &&
        each !== null &&
        "message" in each &&
        typeof each.message === "string" &&
        each.message.includes("not known yet"),
    )
  );
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A page of the brand's voices: the most the list route gives. */
const PAGE = 100;

/**
 * The brand's Kling voices, every page of them one after another, as the voices' lists read them
 * (`useVoicePages`): the panel lists them all, and a picked voice past the first page keeps its
 * name. One being made is read again every 5 s until it is ready or failed.
 */
export function useVoiceProfilePages(teamId: string, brandId: string, enabled: boolean) {
  const pages = useInfiniteQuery({
    queryKey: [...getListVoiceProfilesQueryKey(teamId, brandId), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listVoiceProfiles(
        teamId,
        brandId,
        { limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.items.some((each) => each.status === "making"))
        ? 5000
        : false,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pages;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return pages;
}

export type VoiceProfilePages = ReturnType<typeof useVoiceProfilePages>;

export interface KlingVoicesPanelProps {
  teamId: string;
  brandId: string;
  /** The brand's voices, every page (`useVoiceProfilePages`), read once for the composer. */
  profiles: VoiceProfilePages;
  /** The catalog's row that makes a voice (`voice.profile`), or none: Create Voice is off. */
  model: string | null;
  /** The voices the draft names, in their `@Voice1` order. */
  picked: string[];
  /** The most voices the model takes. */
  room: number;
  onToggle: (id: string) => void;
  onClose: () => void;
}

/**
 * Kling 4.0's Voice Settings (spec G2-kling-v4-voices, layout A, Daniil 2026-11-03), on live's
 * Voice Settings panel (`VoicePanel`): the brand's Kling voices to pick, numbered in their
 * `@Voice1` order, and Create Voice, a clip uploaded to the library and made a voice. Live has no
 * equivalent.
 */
export function KlingVoicesPanel(props: KlingVoicesPanelProps) {
  const { teamId, brandId, profiles, model, picked, room, onToggle, onClose } = props;
  const [source, setSource] = useState<Source>("voices");
  useWindowEvent("keydown", (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    const at = event.target instanceof Element ? event.target : null;
    // A list open in it, or a dialog over it (a delete's confirmation), takes its own Escape.
    if (at?.closest('[aria-expanded="true"], [role="listbox"], [role="dialog"]')) return;
    onClose();
  });
  return (
    <section className={classes.panel} aria-label="Voice Settings">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.title}>Voice Settings</Text>
        <CloseButton aria-label="Close the voice settings" onClick={onClose} />
      </Group>
      <Select
        aria-label="Voice source"
        data={SOURCES}
        value={source}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
        onChange={(next) => {
          if (next !== null) setSource(next as Source);
        }}
      />
      {source === "voices" ? (
        <VoiceProfiles
          teamId={teamId}
          brandId={brandId}
          list={profiles}
          picked={picked}
          room={room}
          onToggle={onToggle}
        />
      ) : (
        <CreateVoiceProfile
          teamId={teamId}
          brandId={brandId}
          model={model}
          onStarted={() => setSource("voices")}
        />
      )}
    </section>
  );
}

interface VoiceProfilesProps {
  teamId: string;
  brandId: string;
  list: VoiceProfilePages;
  picked: string[];
  room: number;
  onToggle: (id: string) => void;
}

/** The brand's Kling voices: a ready one picks or unpicks, one being made shows it. */
function VoiceProfiles({ teamId, brandId, list, picked, room, onToggle }: VoiceProfilesProps) {
  const queryClient = useQueryClient();
  // A delete asks first, as live's actors' does (`MyActors`); a picked voice leaves the video.
  const [deleting, setDeleting] = useState<VoiceProfile | null>(null);
  const remove = useMutation({
    mutationFn: (profile: VoiceProfile) => deleteVoiceProfile(teamId, brandId, profile.id),
    onSuccess: async (_, profile) => {
      setDeleting(null);
      if (picked.includes(profile.id)) onToggle(profile.id);
      await queryClient.invalidateQueries({
        queryKey: getListVoiceProfilesQueryKey(teamId, brandId),
      });
    },
  });
  if (list.isPending) return <Loader size="sm" aria-label="Loading the voices" />;
  if (list.isError) return <ErrorAlert error={list.error} />;
  const profiles = list.data.pages.flatMap((page) => page.items);
  if (profiles.length === 0) {
    return <Text className={classes.empty}>No Kling voices yet: create one from a clip.</Text>;
  }
  const full = picked.length >= room;
  return (
    <Stack gap={8}>
      <ScrollArea type="scroll" scrollbarSize={6} className={classes.list}>
        <div className={classes.rows}>
          {profiles.map((profile) => (
            <VoiceProfileRow
              key={profile.id}
              profile={profile}
              at={picked.indexOf(profile.id)}
              full={full}
              onToggle={onToggle}
              onDelete={() => setDeleting(profile)}
              onLost={() => void list.refetch()}
            />
          ))}
        </div>
      </ScrollArea>
      {full ? (
        <Text size="xs" c="var(--cai-color-text-muted)">
          Up to {room} voices a video.
        </Text>
      ) : null}
      <ConfirmModal
        opened={deleting !== null}
        title={`Delete voice “${deleting?.name ?? ""}”?`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleting !== null) remove.mutate(deleting);
        }}
        onCancel={() => {
          remove.reset();
          setDeleting(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
      >
        <Text size="sm">
          This action cannot be undone. The voice will be removed from the brand's Kling voices.
        </Text>
      </ConfirmModal>
    </Stack>
  );
}

interface VoiceProfileRowProps {
  profile: VoiceProfile;
  /** Its place among the picked voices, or -1. */
  at: number;
  full: boolean;
  onToggle: (id: string) => void;
  onDelete: () => void;
  onLost: () => void;
}

/** One voice: its sample, its name and length or state, its pick number and its delete. */
function VoiceProfileRow({ profile, at, full, onToggle, onDelete, onLost }: VoiceProfileRowProps) {
  const ready = profile.status === "ready";
  const state =
    profile.status === "making"
      ? "Making the voice…"
      : profile.status === "failed"
        ? "Failed: its job says why"
        : `${Math.round(profile.duration_ms / 1000)} s`;
  return (
    <div className={classes.row} data-chosen={at >= 0 || undefined}>
      {ready && profile.sample_url !== null ? (
        <VoicePlay url={profile.sample_url} name={profile.name} onLost={onLost} />
      ) : profile.status === "making" ? (
        <Loader size="xs" aria-label={`${profile.name} is being made`} />
      ) : null}
      <UnstyledButton
        className={classes.choose}
        aria-label={`${profile.name}, ${state}`}
        aria-pressed={at >= 0}
        disabled={!ready || (at < 0 && full)}
        onClick={() => onToggle(profile.id)}
      >
        <span className={classes.name}>{profile.name}</span>
        <Text span size="xs" c="var(--cai-color-text-muted)">
          {" "}
          {state}
        </Text>
      </UnstyledButton>
      {at >= 0 ? (
        <Badge size="sm" circle aria-label={`Voice ${at + 1}`}>
          {at + 1}
        </Badge>
      ) : null}
      {profile.status !== "making" ? (
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={`Delete ${profile.name}`}
          onClick={onDelete}
        >
          <Trash2 size={14} aria-hidden />
        </ActionIcon>
      ) : null}
    </div>
  );
}

interface CreateVoiceProfileProps {
  teamId: string;
  brandId: string;
  model: string | null;
  /** The voice's job started: the list shows it being made. */
  onStarted: () => void;
}

/**
 * Create Voice: a clip uploaded to the brand's library, quoted once the worker has read its
 * length, then made a voice for the quoted credits (`voice.profile`, 2 credits, Daniil).
 */
function CreateVoiceProfile({ teamId, brandId, model, onStarted }: CreateVoiceProfileProps) {
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [name, setName] = useState("");
  const [clip, setClip] = useState<Asset | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const resetChooser = useRef<() => void>(null);
  // The clip a voice would be made from now: a quote for any other comes too late to count.
  const current = useRef<string | null>(null);
  const bodyOf = (asset: Asset, voiceName: string) => ({
    type: "voice.profile" as const,
    model: model ?? "",
    source: { kind: "asset" as const, asset_id: asset.id },
    name: voiceName,
  });
  const quote = async (asset: Asset) => {
    setProblem(null);
    for (let tried = 1; ; tried++) {
      try {
        // The price is the voice's own, whatever its name: an unnamed clip quotes under its file's.
        const quoted = await quoteJob(
          teamId,
          brandId,
          bodyOf(asset, (name.trim() || asset.name).slice(0, MAX_NAME)),
        );
        if (current.current === asset.id) setCredits(quoted.credits_max);
        return;
      } catch (error) {
        if (current.current !== asset.id) return;
        if (!notMeasuredYet(error) || tried >= MEASURE_TRIES) {
          setProblem(toUserMessage(error));
          return;
        }
        await wait(MEASURE_WAIT_MS);
        if (current.current !== asset.id) return;
      }
    }
  };
  const upload = useMediaUpload({
    teamId,
    brandId,
    kinds: CLIP_KINDS,
    onUploaded: (asset) => {
      current.current = asset.id;
      setClip(asset);
      setCredits(null);
      void quote(asset);
    },
    // Another clip going up replaces the one before at once: nothing is made from that one.
    // (The hook calls this with null once an upload ends, after `onUploaded`.)
    onUploading: (controller) => {
      if (controller === null) return;
      current.current = null;
      setClip(null);
      setCredits(null);
      setProblem(null);
    },
  });
  const start = useMutation({
    mutationFn: async () => {
      if (clip === null || credits === null) throw new Error("No clip to make a voice from");
      const data = { ...bodyOf(clip, name.trim()), expected_quote_max: credits };
      const key = idempotency.keyFor(data);
      try {
        return await startJob(teamId, brandId, data, { "Idempotency-Key": key });
      } catch (error) {
        idempotency.failed(key, data, error);
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getListVoiceProfilesQueryKey(teamId, brandId),
      });
      setClip(null);
      setCredits(null);
      setName("");
      onStarted();
    },
  });
  if (model === null) {
    return <Text className={classes.empty}>Making voices is not offered here yet.</Text>;
  }
  const ready = clip !== null && credits !== null && name.trim().length > 0;
  return (
    <Stack gap={8}>
      <FileButton
        accept={CLIP_TYPES.join(",")}
        resetRef={resetChooser}
        onChange={(file) => {
          void upload.send(file);
          resetChooser.current?.();
        }}
        inputProps={{ "aria-label": "Choose a clip to make a voice from" }}
      >
        {(chooser) => (
          <Button
            {...chooser}
            variant="light"
            leftSection={<Upload size={16} aria-hidden />}
            loading={upload.pending}
          >
            {clip === null ? "Upload a clip" : clip.name}
          </Button>
        )}
      </FileButton>
      <Text size="xs" c="var(--cai-color-text-muted)">
        {CLIP_RULES}
      </Text>
      <UploadState upload={upload} />
      <TextInput
        label="Name"
        value={name}
        maxLength={MAX_NAME}
        onChange={(event) => setName(event.currentTarget.value)}
      />
      {problem === null ? null : (
        <Text size="sm" c="var(--cai-color-danger)">
          {problem}
        </Text>
      )}
      <Button disabled={!ready} loading={start.isPending} onClick={() => start.mutate()}>
        {credits === null ? "Create voice" : `Create voice · ${credits} credits`}
      </Button>
      {start.isError ? <ErrorAlert error={start.error} /> : null}
    </Stack>
  );
}
